/** The reports filter row: month+year DatePicker (apply on accept/close only, no
 *  debounce), an "All year" switch, report-type select, and an optional single-shop
 *  filter (the server accepts only one shop_id; re-picking the applied value is a no-op). */
import { useState } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import type { ReportType } from '../../api/index.ts';
import { REPORT_TYPE_OPTIONS } from './periodOptions.ts';

/** Sentinel Select value for "All shops" (shop_id omitted from API calls). */
const ALL_SHOPS_VALUE = '';

/** The minimal shop shape the filter needs -- ShopSummary is structurally
 *  assignable, so ReportsPage passes its existing shops fetch straight in. */
export interface ShopFilterOption {
  id: number;
  name: string;
}

export interface PeriodFilterProps {
  year: number;
  /** null = "All year" (month omitted from the API call). */
  month: number | null;
  reportType: ReportType;
  showReportType: boolean;
  onYearChange: (year: number) => void;
  onMonthChange: (month: number | null) => void;
  onReportTypeChange: (reportType: ReportType) => void;
  /** Select renders only when onShopChange is provided AND shops is non-empty. */
  shops?: readonly ShopFilterOption[];
  shopId?: number | null;
  onShopChange?: (shopId: number | null) => void;
}

export function PeriodFilter({
  year,
  month,
  reportType,
  showReportType,
  onYearChange,
  onMonthChange,
  onReportTypeChange,
  shops,
  shopId = null,
  onShopChange,
}: Readonly<PeriodFilterProps>): React.JSX.Element {
  const allYear = month === null;

  // Remembers the last non-null month so "All year" off restores the prior selection.
  const [lastMonth, setLastMonth] = useState(month ?? new Date().getMonth() + 1);
  if (month !== null && month !== lastMonth) {
    setLastMonth(month);
  }

  const pickerValue = dayjs(new Date(year, (month ?? lastMonth) - 1, 1));

  // Uncommitted in-calendar state; onAccept alone commits it. Dismissing without
  // accepting throws the draft away.
  const [draft, setDraft] = useState<Dayjs | null>(null);

  function handleAccept(newValue: Dayjs | null): void {
    setDraft(null);
    if (!newValue?.isValid()) return;
    onYearChange(newValue.year());
    onMonthChange(newValue.month() + 1);
  }

  function handleShopChange(e: SelectChangeEvent): void {
    const next = e.target.value === ALL_SHOPS_VALUE ? null : Number(e.target.value);
    // Re-picking the applied value is a no-op -- never refetches.
    if (next !== shopId && onShopChange) onShopChange(next);
  }

  const showShopFilter = onShopChange !== undefined && shops !== undefined && shops.length > 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <DatePicker
          label="Period"
          views={['year', 'month']}
          openTo="month"
          disableFuture
          value={draft ?? pickerValue}
          onChange={setDraft}
          onAccept={handleAccept}
          onClose={() => setDraft(null)}
          disabled={allYear}
          slotProps={{
            // field readOnly: calendar-only input (see file header for why).
            field: { readOnly: true },
            textField: {
              size: 'small',
              sx: { minWidth: 180 },
              slotProps: { htmlInput: { 'aria-label': 'reports-period' } },
            },
          }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={allYear}
              onChange={(e) => onMonthChange(e.target.checked ? null : lastMonth)}
              slotProps={{ input: { 'aria-label': 'reports-all-year' } }}
            />
          }
          label="All year"
        />

        {showReportType && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="reports-type-label">Report type</InputLabel>
            <Select
              labelId="reports-type-label"
              label="Report type"
              value={reportType}
              onChange={(e) => onReportTypeChange(e.target.value)}
              inputProps={{ 'aria-label': 'reports-report-type' }}
            >
              {REPORT_TYPE_OPTIONS.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {showShopFilter && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="reports-shop-label">Shop</InputLabel>
            <Select
              labelId="reports-shop-label"
              label="Shop"
              value={shopId === null ? ALL_SHOPS_VALUE : String(shopId)}
              onChange={handleShopChange}
              inputProps={{ 'aria-label': 'reports-shop' }}
            >
              <MenuItem value={ALL_SHOPS_VALUE}>All shops</MenuItem>
              {shops.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default PeriodFilter;
